import { useEffect, useState } from "react";
import { Sun, Cloud, CloudSun, CloudRain, CloudDrizzle, CloudFog, CloudLightning, Snowflake, Wind, Droplets, CloudAlert } from "lucide-react";
import { api } from "../lib/api";

const ICONS = { sun: Sun, cloud: Cloud, "cloud-sun": CloudSun, "cloud-rain": CloudRain, "cloud-drizzle": CloudDrizzle, "cloud-fog": CloudFog, "cloud-lightning": CloudLightning, snowflake: Snowflake };

const fmtDay = (iso) => new Date(iso + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

export function WeatherPanel({ city, state, date, weatherPolicy }) {
  const [w, setW] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!city) { setErr(true); return; }
    api.get(`/weather?city=${encodeURIComponent(city)}&state=${encodeURIComponent(state || "")}`)
      .then((r) => setW(r.data)).catch(() => setErr(true));
  }, [city, state]);

  if (err || !city) return null;
  if (!w) return null;

  const day = date ? w.daily.find((d) => d.date === date) : null;
  if (date && !day) return null;

  const dependent = (weatherPolicy || "").toLowerCase().includes("weather");
  const Icon = ICONS[(day || w.current).icon] || Cloud;

  return (
    <div className="bg-[#111] border border-white/10 rounded-2xl p-6" data-testid="weather-panel">
      <p className="overline mb-3">{day ? `Forecast · ${fmtDay(day.date)}` : "Weather now"} · {w.city}</p>
      <div className="flex items-center gap-4">
        <Icon size={42} className="text-sky-300 shrink-0" />
        <div>
          <p className="font-display font-black text-3xl leading-none" data-testid="weather-temp">
            {day ? `${day.tmax}°` : `${w.current.temp}°F`}
            {day && <span className="text-lg text-zinc-500 font-bold"> / {day.tmin}°</span>}
          </p>
          <p className="text-sm text-zinc-300 mt-1">{(day || w.current).condition}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-xs text-zinc-400">
        {day
          ? <span className="flex items-center gap-1"><Droplets size={12} /> {day.rain_chance}% rain chance</span>
          : (<>
            <span className="flex items-center gap-1"><Wind size={12} /> {w.current.wind} mph</span>
            <span className="flex items-center gap-1"><Droplets size={12} /> {w.current.humidity}% humidity</span>
            {w.daily[0] && <span className="flex items-center gap-1"><CloudRain size={12} /> {w.daily[0].rain_chance}% rain today</span>}
          </>)}
      </div>
      {w.ai_tip && !day && <p className="text-xs text-sky-200/80 italic mt-3 border-t border-white/5 pt-3" data-testid="weather-tip">{w.ai_tip}</p>}
      {dependent && (
        <p className="text-xs text-amber-300 mt-3 flex items-start gap-1.5" data-testid="weather-dependent-note">
          <CloudAlert size={13} className="shrink-0 mt-0.5" /> Weather-dependent — confirm before you go.
        </p>
      )}
    </div>
  );
}
